import { DestroyRef, EventEmitter, inject, input, signal } from '@angular/core';
import { DynamicFieldsetModule, ModelFormServiceFactory } from '@common/forms/frontend-shared';
import { InputCheckboxInstruction, InputTextInstruction, StaticTextInstruction } from '@common/instructions/frontend-shared';
import { AppMessagesComponent } from '@common/messages/frontend-shared';
import { UiButtonComponent } from '@common/ui/frontend-shared/button';
import { UiIconsModule } from '@common/ui/frontend-shared/icons';
import { UiInputGroupComponent } from '@common/ui/frontend-shared/input-group';
import { CompanyAddressType } from '@common/users/shared';
import { AccessState, AppMessage, AppRequestService, ConfigService, CoreFrontendSharedModule, LocalMessageHandlerService } from '@core/frontend-shared';
import { CompanyOnboardingRequest } from '@mpf/users/shared';
import { Store } from '@ngxs/store';
import { DynamicDialogRef } from 'prime-ng-old/dynamicdialog';
import { of, switchMap, throwError } from 'rxjs';
import * as i0 from "@angular/core";
import * as i1 from "../../../../../common/forms/frontend-shared/src/lib/simple-fieldset/simple-fieldset.component";
import * as i2 from "../../../../../common/forms/frontend-shared/src/lib/form-submit-button/form-submit-button.directive";
const _c0 = () => ({
  label: "Daten senden"
});
function CompanyCompleteOnboardingComponent_Conditional_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "div")(1, "p");
    i0.ɵɵtext(2, "Wir ben\u00F6tigen noch einige Angaben von Ihnen.");
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(3, "p");
    i0.ɵɵtext(4, "Sobald wir diese bearbeitet haben, meldet sich unser Supportteam bei Ihnen, um das Onboarding abzuschlie\u00DFen.");
    i0.ɵɵelementEnd();
    i0.ɵɵelement(5, "simple-fieldset", 0)(6, "app-messages");
    i0.ɵɵelementStart(7, "div", 1)(8, "ui-input-group");
    i0.ɵɵelement(9, "button", 2);
    i0.ɵɵelementEnd()()();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵadvance(5);
    i0.ɵɵproperty("formService", ctx_r0.formService)("instructions", ctx_r0.instructions);
    i0.ɵɵadvance(4);
    i0.ɵɵproperty("formSubmitButton", ctx_r0.formService)("formSubmitButtonOptions", i0.ɵɵpureFunction0(4, _c0));
  }
}
function CompanyCompleteOnboardingComponent_Conditional_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "div")(1, "h3");
    i0.ɵɵtext(2, "Vielen Dank f\u00FCr Ihre Anmeldung!");
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(3, "p");
    i0.ɵɵtext(4, "Unser Supportteam bearbeitet derzeit Ihre Daten und schaltet Ihren Account frei.");
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(5, "p");
    i0.ɵɵtext(6, "Sobald dies geschehen ist, erhalten Sie von uns eine Information per Mail!");
    i0.ɵɵelementEnd()();
  }
}
export let CompanyCompleteOnboardingComponent = /*#__PURE__*/(() => {
  class CompanyCompleteOnboardingComponent {
    constructor() {
      this.destroyRef = inject(DestroyRef);
      this.formService = inject(ModelFormServiceFactory).useDestroyRef(this.destroyRef).createModelForm(CompanyOnboardingRequest);
      this.api = inject(AppRequestService);
      this.messages = inject(LocalMessageHandlerService);
      this.config = inject(ConfigService);
      this.store = inject(Store);
      this.dialogRef = inject(DynamicDialogRef, {
        optional: true
      });
      this.submitCompleted = signal(false, ...(ngDevMode ? [{
        debugName: "submitCompleted"
      }] : /* istanbul ignore next */[]));
      this.displayMode = input(...(ngDevMode ? [undefined, {
        debugName: "displayMode"
      }] : /* istanbul ignore next */[]));
      this.onComplete = new EventEmitter();
    }
    ngOnInit() {
      this.formService.setEditorDefinition('default');
      this.formService.setSaveHandler({
        returnsUpdatedFormData: false,
        execute: data => {
          return this.executeSubmit(data);
        }
      });
      this.instructions = this.getInstructions();
      const company = this.store.selectSnapshot(AccessState.accessData('company'));
      const user = this.store.selectSnapshot(AccessState.accessData('user'));
      this.formService.setInitialValue({
        companyAddress: {
          type: CompanyAddressType.Postal,
          companyName: company.name,
          personName: user.name
        },
        billingAddress: {
          type: CompanyAddressType.Billing,
          companyName: company.name,
          personName: user.name
        },
        contactName: user.name,
        contactEmail: user.email
      });
      this.formService.initialize();
    }
    // if observable resolves to true this indicates form is complete and parent can continue navigation
    executeSubmit(data) {
      return this.validateForm(data).pipe(switchMap(validationResult => {
        if (validationResult === true) {
          this.api.sendRequest(CompanyOnboardingRequest, data).subscribe({
            next: response => {
              this.messages.create().setSummary('Vielen Dank!').setMessage('Unser Team meldet sich bei Ihnen, sobald die nötigen Schritte durchgeführt wurden.').setSeverity('success');
              this.submitCompleted.set(true);
              this.onComplete.emit();
              // if(this.dialogRef) this.dialogRef.close();
            },
            error: err => {
              if (err instanceof AppMessage) this.messages.add(err);else this.messages.createAndDisplay(err);
            }
          });
          return of(true);
        } else {
          return throwError(() => validationResult);
        }
      }));
    }
    validateForm(data) {
      return of(true);
    }
    getInstructions() {
      const compactLayout = true;
      const labelProp = compactLayout ? 'placeholder' : 'label';
      const instructions = [];
      instructions.push(new StaticTextInstruction({
        classes: "ui-margin-top",
        text: "<h4>Unternehmensanschrift</h4>"
      }), new InputTextInstruction({
        formControlName: 'companyAddress.companyName',
        [labelProp]: "Name des Unternehmens"
      }), new InputTextInstruction({
        formControlName: 'companyAddress.personName',
        [labelProp]: "Zuständige Person"
      }), new InputTextInstruction({
        formControlName: 'companyAddress.address',
        [labelProp]: "Straße und Hausnummer"
      }), new InputTextInstruction({
        formControlName: 'companyAddress.zipcode',
        [labelProp]: "Postleitzahl"
      }), new InputTextInstruction({
        formControlName: 'companyAddress.city',
        [labelProp]: "Ort"
      }));
      const conditionalIfBillingAddressEnabled = {
        display: (model, mode) => {
          return !!model.specifyBillingAddress;
        }
      };
      instructions.push(new InputCheckboxInstruction({
        formControlName: 'specifyBillingAddress',
        infoText: "Abweichende Rechnungsanschrift angeben"
      }), new StaticTextInstruction({
        conditional: conditionalIfBillingAddressEnabled,
        classes: "ui-margin-top",
        text: "<h4>Rechnungsanschrift</h4>"
      }), new InputTextInstruction({
        conditional: conditionalIfBillingAddressEnabled,
        formControlName: 'billingAddress.companyName',
        [labelProp]: "Name des Unternehmens"
      }), new InputTextInstruction({
        conditional: conditionalIfBillingAddressEnabled,
        formControlName: 'billingAddress.personName',
        [labelProp]: "Zuständige Person"
      }), new InputTextInstruction({
        conditional: conditionalIfBillingAddressEnabled,
        formControlName: 'billingAddress.address',
        [labelProp]: "Straße und Hausnummer"
      }), new InputTextInstruction({
        conditional: conditionalIfBillingAddressEnabled,
        formControlName: 'billingAddress.zipcode',
        [labelProp]: "Postleitzahl"
      }), new InputTextInstruction({
        conditional: conditionalIfBillingAddressEnabled,
        formControlName: 'billingAddress.city',
        [labelProp]: "Ort"
      }));
      instructions.push(new StaticTextInstruction({
        classes: "ui-margin-top",
        text: "<h4>Ansprechpartner</h4><p>Hier können Sie bei Bedarf eine abweichende Ansprechpartner*in angeben.</p>"
      }), new InputTextInstruction({
        formControlName: 'contactName',
        [labelProp]: "Name"
      }), new InputTextInstruction({
        formControlName: 'contactEmail',
        [labelProp]: "E-Mail-Adresse"
      }));
      // new StaticTextInstruction({ classes:"ui-margin-top",text:"<h4>Angaben für Ihren Account</h4>", }),
      // new InputTextInstruction({ formControlName:'email', [labelProp]:this.plugin.getLabelForField('email') }),
      // new InputTextInstruction({ formControlName:'phone', [labelProp]:this.plugin.getLabelForField('phone') }),
      // instructions.push(
      // 	new StaticTextInstruction({text:"<br>"}),
      // 	new StaticTextInstruction({text:"Mit Ihrer Registrierung erhalten Sie einen kostenfreien Zugang für 6 Monate zu unseren Pflegekursen. Die Kosten hierfür werden von Ihrer Pflegekasse übernommen."}),
      // 	new StaticTextInstruction({text:"Sie können sich nach diesem Zeitraum erneut anmelden, um den Zugang kostenfrei für weitere sechs Monate zu verlängern. Auch diese Kosten werden von Ihrer Pflegekasse übernommen."}),
      // 	new StaticTextInstruction({text:"In unserer <a href=\"/privacy\" target=\"_blank\">Datenschutzerklärung</a> erfahren Sie, wie wir mit Ihren Daten umgehen.<br><br>"}),
      // 	new InputCheckboxInstruction({ 
      // 		formControlName:'acceptTerms', 
      // 		[labelProp]:compactLayout ? undefined : this.plugin.getLabelForField('acceptTerms'), 
      // 		infoText:"Ich habe die <a href=\"/terms\">Allgemeinen Geschäftsbedingungen</a> gelesen und akzeptiere sie."
      // 	})
      // )
      return instructions;
    }
    static {
      this.ɵfac = function CompanyCompleteOnboardingComponent_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || CompanyCompleteOnboardingComponent)();
      };
    }
    static {
      this.ɵcmp = /*@__PURE__*/i0.ɵɵdefineComponent({
        type: CompanyCompleteOnboardingComponent,
        selectors: [["ng-component"]],
        inputs: {
          displayMode: [1, "displayMode"]
        },
        outputs: {
          onComplete: "onComplete"
        },
        features: [i0.ɵɵProvidersFeature([LocalMessageHandlerService])],
        decls: 2,
        vars: 2,
        consts: [[3, "formService", "instructions"], [1, "ui-margin", "ui-align-center"], ["uiButton", "", 1, "ui-button-primary", 3, "formSubmitButton", "formSubmitButtonOptions"]],
        template: function CompanyCompleteOnboardingComponent_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵconditionalCreate(0, CompanyCompleteOnboardingComponent_Conditional_0_Template, 10, 5, "div");
            i0.ɵɵconditionalCreate(1, CompanyCompleteOnboardingComponent_Conditional_1_Template, 7, 0, "div");
          }
          if (rf & 2) {
            i0.ɵɵconditional(!ctx.submitCompleted() ? 0 : -1);
            i0.ɵɵadvance();
            i0.ɵɵconditional(ctx.submitCompleted() ? 1 : -1);
          }
        },
        dependencies: [CoreFrontendSharedModule, DynamicFieldsetModule, i1.SimpleFieldsetComponent, i2.FormSubmitButtonDirective, UiButtonComponent, UiInputGroupComponent, AppMessagesComponent, UiIconsModule],
        encapsulation: 2,
        changeDetection: 0
      });
    }
  }
  return CompanyCompleteOnboardingComponent;
})();